// The brand's own account screen — and, until now, the missing first step.
//
// A brand account with no BrandProfile cannot create a campaign, start a
// collaboration or be invoiced: every brand-side query scopes by
// `brandProfile: { ownerUserId }` and matches nothing. The plumbing for many
// brands per user existed, but nothing in the app ever created even one, so a
// new brand hit "You need a brand profile before working with campaigns" and
// had nowhere to go. This is that screen.
//
// It is also where an agency adds its second client, which is what makes the
// BrandSwitcher appear at all — it stays hidden below two brands.

import { useEffect, useState } from "react";
import { Building2, Check, Loader2, Plus, X } from "lucide-react";
import { DetailSkeleton } from "@/components/marketplace";
import { Select } from "@/components/Select";
import { useTranslation } from "@/hooks/useTranslation";
import { useActiveBrand } from "@/hooks/useActiveBrand";
import {
  useCreateBrandProfile,
  useMyBrandProfiles,
  useUpsertBrandProfile,
  type BrandProfile,
} from "@/hooks/useMarketplace";
import { COUNTRIES, COUNTRY_LABELS } from "./constants";

const INPUT_CLASS =
  "w-full px-3 py-2.5 rounded-control border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600/40";
const LABEL_CLASS = "block text-xs font-medium text-neutral-700 mb-1.5";

const CURRENCIES = ["USD", "UYU", "ARS", "MXN", "COP", "CLP"] as const;

const EMPTY = {
  companyName: "",
  websiteUrl: "",
  industry: "",
  description: "",
  country: "UY",
  defaultCurrency: "USD",
};

type Form = typeof EMPTY;

function fromProfile(b: BrandProfile): Form {
  return {
    companyName: b.companyName,
    websiteUrl: b.websiteUrl ?? "",
    industry: b.industry ?? "",
    description: b.description ?? "",
    country: b.country,
    defaultCurrency: b.defaultCurrency,
  };
}

/** Blank strings are "not set", not "set to empty" — send null. */
function toInput(form: Form) {
  return {
    companyName: form.companyName.trim(),
    websiteUrl: form.websiteUrl.trim() || null,
    industry: form.industry.trim() || null,
    description: form.description.trim() || null,
    country: form.country,
    defaultCurrency: form.defaultCurrency,
  };
}

function BrandForm({
  form,
  setForm,
  t,
}: {
  form: Form;
  setForm: (f: Form) => void;
  t: (key: string) => string;
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className={LABEL_CLASS}>{t("marketplace.brand.companyName")}</label>
        <input
          className={INPUT_CLASS}
          value={form.companyName}
          onChange={(e) => setForm({ ...form, companyName: e.target.value })}
          placeholder={t("marketplace.brand.companyNamePlaceholder")}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL_CLASS}>{t("marketplace.brand.country")}</label>
          <Select
            value={form.country}
            onChange={(v) => setForm({ ...form, country: v })}
            options={COUNTRIES.map((c) => ({ value: c, label: COUNTRY_LABELS[c] ?? c }))}
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>{t("marketplace.brand.currency")}</label>
          <Select
            value={form.defaultCurrency}
            onChange={(v) => setForm({ ...form, defaultCurrency: v })}
            options={CURRENCIES.map((c) => ({ value: c, label: c }))}
          />
        </div>
      </div>

      <div>
        <label className={LABEL_CLASS}>{t("marketplace.brand.website")}</label>
        <input
          className={INPUT_CLASS}
          value={form.websiteUrl}
          onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
          placeholder="https://…"
        />
      </div>

      <div>
        <label className={LABEL_CLASS}>{t("marketplace.brand.industry")}</label>
        <input
          className={INPUT_CLASS}
          value={form.industry}
          onChange={(e) => setForm({ ...form, industry: e.target.value })}
          placeholder={t("marketplace.brand.industryPlaceholder")}
        />
      </div>

      <div>
        <label className={LABEL_CLASS}>{t("marketplace.brand.description")}</label>
        <textarea
          className={INPUT_CLASS}
          rows={3}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder={t("marketplace.brand.descriptionPlaceholder")}
        />
      </div>
    </div>
  );
}

export default function BrandProfileEditor() {
  const { t } = useTranslation();
  const { brands, loading } = useMyBrandProfiles();
  const { activeBrandId, setActiveBrand } = useActiveBrand();
  const { save, loading: saving } = useUpsertBrandProfile();
  const { create, loading: creating } = useCreateBrandProfile();

  // Resolved the same way the server does — named brand, else the oldest.
  const editing = (activeBrandId ? brands.find((b) => b.id === activeBrandId) : null) ?? brands[0] ?? null;

  const [form, setForm] = useState<Form>(EMPTY);
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState<Form>(EMPTY);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate when the brand being edited changes, not on every render, so an
  // in-progress edit is never clobbered by a background refetch.
  useEffect(() => {
    if (editing) setForm(fromProfile(editing));
  }, [editing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitEdit = async () => {
    setError(null);
    setSaved(false);
    try {
      await save(toInput(form), editing?.id);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const submitCreate = async () => {
    setError(null);
    try {
      const brand = await create(toInput(newForm));
      // Switch to what they just made: creating a brand and then still acting
      // as the previous one is the kind of thing nobody notices until a
      // campaign lands on the wrong client.
      setActiveBrand(brand.id);
      setAdding(false);
      setNewForm(EMPTY);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  if (loading && brands.length === 0) {
    return (
      <div className="adv-page">
        <DetailSkeleton />
      </div>
    );
  }

  const firstRun = brands.length === 0;

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <h1 className="text-xl font-bold text-neutral-900">
        {firstRun ? t("marketplace.brand.firstRunTitle") : t("marketplace.brand.title")}
      </h1>
      <p className="text-sm text-neutral-500 mt-1">
        {firstRun ? t("marketplace.brand.firstRunSubtitle") : t("marketplace.brand.subtitle")}
      </p>

      {/* Every other brand-side screen is unusable until this exists, so say so
          rather than letting them find out on the campaign form. */}
      {firstRun && (
        <div className="flex items-start gap-2 mt-4 px-4 py-3 rounded-control bg-amber-50 border border-amber-200">
          <Building2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            {t("marketplace.brand.firstRunNotice")}
          </p>
        </div>
      )}

      {error && (
        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-control px-3 py-2">
          {error}
        </p>
      )}

      {firstRun ? (
        <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
          <BrandForm form={newForm} setForm={setNewForm} t={t} />
          <button
            type="button"
            onClick={submitCreate}
            disabled={!newForm.companyName.trim() || creating}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-button text-white bg-gradient-to-r from-steel-600 to-steel-700 hover:opacity-90 disabled:opacity-50 transition-opacity cursor-pointer"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t("marketplace.brand.createFirst")}
          </button>
        </div>
      ) : (
        <>
          {brands.length > 1 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setActiveBrand(b.id)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${
                    b.id === editing?.id
                      ? "bg-steel-600 border-steel-600 text-white"
                      : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"
                  }`}
                >
                  {b.companyName}
                </button>
              ))}
            </div>
          )}

          <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
            <BrandForm form={form} setForm={setForm} t={t} />

            {/* Read-only: the platform take is a term of the account, set by an
                operator. Shown because the brand should know what it is. */}
            <div className="mt-5 pt-4 border-t border-neutral-100">
              <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
                {t("marketplace.campaign.commission")}
              </p>
              <p className="text-sm font-bold text-neutral-900 mt-0.5">{editing?.commissionPct}%</p>
              <p className="text-xs text-neutral-400 mt-0.5">
                {t("marketplace.campaign.commissionFixed")}
              </p>
            </div>

            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={submitEdit}
                disabled={!form.companyName.trim() || saving}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-button text-white bg-gradient-to-r from-steel-600 to-steel-700 hover:opacity-90 disabled:opacity-50 transition-opacity cursor-pointer"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {t("common.save")}
              </button>
              {saved && (
                <span className="text-xs text-emerald-700">{t("marketplace.brand.saved")}</span>
              )}
            </div>
          </div>

          <div className="bg-white border border-neutral-200 rounded-card p-6 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-neutral-900">
                  {t("marketplace.brand.addTitle")}
                </h2>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {t("marketplace.brand.addSubtitle")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAdding(!adding)}
                className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-control border border-neutral-200 text-neutral-700 hover:bg-neutral-50 transition-colors cursor-pointer"
              >
                {adding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                {adding ? t("common.cancel") : t("marketplace.brand.add")}
              </button>
            </div>

            {adding && (
              <div className="mt-5 pt-5 border-t border-neutral-100">
                <BrandForm form={newForm} setForm={setNewForm} t={t} />
                <button
                  type="button"
                  onClick={submitCreate}
                  disabled={!newForm.companyName.trim() || creating}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-button text-white bg-gradient-to-r from-steel-600 to-steel-700 hover:opacity-90 disabled:opacity-50 transition-opacity cursor-pointer"
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {t("marketplace.brand.add")}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
